import { useCallback, useEffect, useRef, useState } from 'react';

const MUSIC_SOURCE = '/media/betaris-background-music.mp3';

export const useBackgroundMusic = () => {
  const audioRef = useRef(null);
  const wanted = useRef(false);
  const operation = useRef(0);
  const [status, setStatus] = useState('off');
  const [error, setError] = useState('');

  const start = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const current = ++operation.current;
    wanted.current = true;
    setError('');
    setStatus('loading');
    // Assign the source only inside an explicit user action (or a previously
    // authorized visibility resume). No unsolicited playback or initial download.
    if (!audio.getAttribute('src')) audio.src = MUSIC_SOURCE;
    if (audio.error) audio.load();
    audio.volume = .55;
    audio.muted = false;
    audio.play()?.catch(reason => {
      if (current !== operation.current || reason.name === 'AbortError') return;
      wanted.current = false;
      setStatus('error');
      setError(reason.name === 'NotAllowedError'
        ? 'Ihr Browser hat die Wiedergabe blockiert. Bitte tippen Sie erneut auf die Musik-Taste.'
        : 'Die Musik konnte nicht geladen werden. Bitte versuchen Sie es erneut.');
    });
  }, []);

  const pause = useCallback(() => {
    wanted.current = false;
    operation.current += 1;
    audioRef.current?.pause();
    setStatus('paused');
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    const visibility = () => {
      if (document.hidden) {
        if (wanted.current) {
          operation.current += 1;
          audio?.pause();
          setStatus('paused');
        }
      } else if (wanted.current) start();
    };
    document.addEventListener('visibilitychange', visibility);
    return () => {
      wanted.current = false;
      operation.current += 1;
      document.removeEventListener('visibilitychange', visibility);
      audio?.pause();
    };
  }, [start]);

  const onPlaying = () => {
    if (!wanted.current || document.hidden) { audioRef.current?.pause(); return; }
    setError('');
    setStatus('playing');
  };
  const onError = () => {
    wanted.current = false;
    operation.current += 1;
    audioRef.current?.pause();
    setStatus('error');
    setError('Die Musik konnte nicht geladen werden. Bitte versuchen Sie es erneut.');
  };
  return {
    audioRef, status, error, onPlaying, onError,
    onPause: () => setStatus(previous => previous === 'error' ? 'error' : 'paused'),
    onWaiting: () => { if (wanted.current && !document.hidden) setStatus('loading'); },
    toggle: () => { if (status === 'playing' || status === 'loading') pause(); else start(); },
  };
};